/* Header search opens the vehicle filters only when the entire query is exactly a
 * make or model in stock. Names come from the MAX search index (one cached request);
 * Shopify's search pages remain the fallback when it cannot be reached. */
(() => {
  if (customElements.get('max-vehicle-search')) return;
  const FORM_SELECTOR = 'form.header-search-global-form, .template-search-search-bar form.search';
  const PARAM = 'filter.p.m.custom.';
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  const values = value => [...new Set((Array.isArray(value) ? value : [value]).filter(v => typeof v === 'string' && v.trim()).map(v => v.trim()))];
  const aliases = {mercedesbenz:['benz','mercedes'], volkswagen:['vw'], kgm:['ssangyong'], chevrolet:['chevy'], dsautomobiles:['ds']};
  const makeKeys = make => [normalize(make), ...(aliases[normalize(make)] || [])];

  class VehicleSearch extends HTMLElement {
    static match(query, rows, complete) {
      const key = normalize(query);
      if (!key) return null;
      const makes = new Map(), models = new Map();
      for (const row of rows) for (const make of values(row.make)) {
        const keys = makeKeys(make);
        if (keys.includes(key)) makes.set(normalize(make), {make});
        for (const model of values(row.model)) {
          const modelKey = normalize(model);
          if (key === modelKey || keys.some(k => key === k + modelKey)) {
            models.set(normalize(make) + '\n' + modelKey, {make, model});
          }
        }
      }
      if (makes.size === 1) return [...makes.values()][0];
      if (makes.size > 1 || !complete || models.size !== 1) return null;
      return [...models.values()][0];
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._cache = new Map();
      this._events = new AbortController();
      const options = {capture:true, signal:this._events.signal};
      document.addEventListener('submit', event => {
        const form = event.target;
        if (!form.matches?.(FORM_SELECTOR) || !this.enabled()) return;
        const selected = form.closest('predictive-search')?.querySelector('[aria-selected="true"] a[href], a[aria-selected="true"][href]');
        if (selected && new URL(selected.href, location.href).pathname !== new URL(this.dataset.searchUrl, location.origin).pathname) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.run(form);
      }, options);
      document.addEventListener('click', event => {
        const link = event.target.closest?.('a[href]');
        const form = link?.closest(FORM_SELECTOR);
        if (!form || !this.enabled() || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.pathname !== new URL(this.dataset.searchUrl, location.origin).pathname || !url.searchParams.has('q')) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.run(form, url.searchParams.get('q'));
      }, options);
      // Fetch the make/model list while the visitor is still typing.
      document.addEventListener('focusin', event => {
        if (event.target.name === 'q' && event.target.closest(FORM_SELECTOR) && this.enabled() && this.dataset.apiUrl) this.inventory(new AbortController().signal).catch(() => {});
      }, options);
      document.addEventListener('input', event => {
        if (event.target.name === 'q' && event.target.closest(FORM_SELECTOR)) {
          this._serial = (this._serial || 0) + 1;
          this._request?.abort();
          this.clearBusy();
        }
      }, options);
    }

    disconnectedCallback() {
      this._connected = false;
      this._serial = (this._serial || 0) + 1;
      this._events?.abort(); this._request?.abort(); this.clearBusy();
    }

    enabled() {
      if (this.dataset.routerEnabled !== 'true' || !this.dataset.collectionUrl) return false;
      try {
        const target = new URL(this.dataset.collectionUrl, location.origin);
        const source = new URL(this.dataset.searchUrl, location.origin);
        return target.origin === location.origin && /\/collections\/[^/]+\/?$/.test(target.pathname) && source.origin === location.origin && /\/search\/?$/.test(source.pathname);
      } catch { return false; }
    }

    static nativeURL(form, searchURL, query) {
      const url = new URL(searchURL, location.origin);
      url.search = new URLSearchParams(new FormData(form)).toString();
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.delete('view');
      url.searchParams.delete('page');
      return url;
    }

    clearBusy() {
      if (!this._busy) return;
      this._busy.form.removeAttribute('aria-busy');
      for (const [button, disabled] of this._busy.buttons) button.disabled = disabled;
      this._busy = null;
    }

    inventory(signal) {
      if (this._rows && Date.now() - this._rows.at < 60000) return Promise.resolve(this._rows.rows);
      this._pending ||= this.fetchInventory().finally(() => { this._pending = null; });
      return new Promise((resolve, reject) => {
        const stop = () => reject(new DOMException('Aborted', 'AbortError'));
        if (signal.aborted) return stop();
        signal.addEventListener('abort', stop, {once:true});
        this._pending.then(resolve, reject).finally(() => signal.removeEventListener('abort', stop));
      });
    }

    async fetchInventory() {
      if (!this.dataset.apiUrl) throw new Error('No search API');
      const api = new URL(this.dataset.apiUrl);
      api.searchParams.set('c', new URL(this.dataset.collectionUrl, location.origin).pathname.split('/').filter(Boolean).pop());
      api.searchParams.set('per', '1');
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(api, {credentials:'omit', signal:controller.signal});
        if (!response.ok) throw new Error('Search API unavailable');
        const data = await response.json();
        if (!data.mm || typeof data.mm !== 'object') throw new Error('Invalid search inventory');
        const rows = [];
        for (const [make, models] of Object.entries(data.mm)) {
          rows.push({make});
          for (const [model] of models) if (typeof model === 'string') rows.push({make, model});
        }
        this._rows = {at:Date.now(), rows};
        return rows;
      } finally { clearTimeout(timer); }
    }

    async resolve(query, controller) {
      const cached = this._cache.get(query);
      if (cached && Date.now() - cached.at < 60000) return cached.match;
      let match;
      try { match = VehicleSearch.match(query, await this.inventory(controller.signal), true); }
      catch (error) {
        if (controller.signal.aborted) throw error;
        return this.crawl(query, controller);
      }
      this._cache.set(query, {at:Date.now(), match});
      if (this._cache.size > 20) this._cache.delete(this._cache.keys().next().value);
      return match;
    }

    // Fallback: make/model names from Shopify's own search result pages (slower).
    async crawl(query, controller) {
      const cached = this._cache.get(query);
      if (cached && Date.now() - cached.at < 60000) return cached.match;
      const url = new URL(this.dataset.searchUrl, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('view', 'max-facet-tree');
      url.searchParams.set('sort_by', 'relevance');
      const rows = [], ids = new Set();
      let total, pages = 1;
      for (let page = 1; page <= pages; page++) {
        url.searchParams.set('page', String(page));
        const response = await fetch(url, {credentials:'same-origin', signal:controller.signal});
        if (!response.ok) throw new Error('Search lookup unavailable');
        const data = JSON.parse(await response.text());
        if (data.version !== 1 || data.kind !== 'search' || String(data.query).trim() !== query || data.page !== page || !Number.isInteger(data.total) || data.total < 0 || !Number.isInteger(data.pages) || data.pages < 0 || !Array.isArray(data.rows) || data.rows.length > 250) throw new Error('Invalid search inventory');
        if (page === 1) {total = data.total; pages = Math.max(1, data.pages);}
        else if (total !== data.total || pages !== Math.max(1, data.pages)) throw new Error('Inventory changed during lookup');
        for (const row of data.rows) {
          if (typeof row.id !== 'string' || !row.id || ids.has(row.id)) throw new Error('Incomplete search inventory');
          for (const field of ['make','model']) {
            const list = Array.isArray(row[field]) ? row[field] : [row[field]];
            if (list.some(v => v != null && typeof v !== 'string')) throw new Error('Invalid vehicle metadata');
          }
          ids.add(row.id); rows.push(row);
        }
        // A whole brand name is unambiguous without downloading every vehicle.
        const brand = VehicleSearch.match(query, rows, false);
        if (brand) return brand;
        // Broad text searches fall back immediately; don't crawl an entire catalog.
        if (pages > 8 || total > 2000) return null;
      }
      if (rows.length !== total) throw new Error('Incomplete search inventory');
      const match = VehicleSearch.match(query, rows, true);
      this._cache.set(query, {at:Date.now(), match});
      if (this._cache.size > 20) this._cache.delete(this._cache.keys().next().value);
      return match;
    }

    async run(form, requestedQuery) {
      this._request?.abort(); this.clearBusy();
      const serial = this._serial = (this._serial || 0) + 1;
      const input = form.querySelector('[name="q"]');
      const query = String(requestedQuery ?? input?.value ?? '').trim();
      const original = VehicleSearch.nativeURL(form, this.dataset.searchUrl, query);
      const target = new URL(this.dataset.collectionUrl, location.origin);
      if (!query) {location.assign(target.href); return;}
      const controller = this._request = new AbortController();
      this._busy = {form, buttons:[...form.querySelectorAll('[type="submit"]')].map(button => [button, button.disabled])};
      form.setAttribute('aria-busy','true');
      for (const [button] of this._busy.buttons) button.disabled = true;
      const timeout = setTimeout(() => controller.abort(), 6000);
      let destination = original;
      try {
        const match = await this.resolve(query, controller);
        if (match) {
          target.searchParams.set(PARAM + 'make', match.make);
          if (match.model) target.searchParams.set(PARAM + 'model', match.model);
          destination = target;
        }
      } catch { /* A missing template, timeout or uncertain match retains native search. */ }
      finally {clearTimeout(timeout); if (serial === this._serial) this.clearBusy();}
      if (serial === this._serial && this.isConnected) location.assign(destination.href);
    }
  }
  customElements.define('max-vehicle-search', VehicleSearch);
})();
